import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
 Container,
 Box,
 Heading,
 Flex,
 useToast,
 Button,
 Center,
 HStack,
 Text,
} from '@chakra-ui/react';
import { InputText, YearPickerInput } from '../../../components/Input';
import Loader from '../../../components/Feedback/Loader';
import baseService from '../../../services/base';
import { CancelModal, SuccessModal } from '../../../components/Modals';
import { fetchData } from '../../../services/fetch';
import { useCertificationForm } from '../../../hooks/useCertificationForm';
// EG-42/AD-8: o rádio "Possui pilares?" e o "Número de pilares" saíram. A forma
// do certificado passou a ser derivada do que os templates trazem, e a coluna
// survey.has_pillar é gravada na criação — ela decide também o MODELO DE NOTA
// (cutoff global x por pilar), não só a tela.
import SelecaoDeTemplates from '../../../components/Questionnaires/SelecaoDeTemplates';
import EstruturaDerivada from '../../../components/Questionnaires/EstruturaDerivada';
import {
  derivarEstruturaDeTemplates,
  idsParaGravar,
} from '../../../core/estrutura/derivarEstruturaDeTemplates';
import { useCatalogoDeClassificacao } from '../../../hooks/useCatalogoDeClassificacao';

export default function CertificationRegister() {
 const toast = useToast();
 const navigate = useNavigate();

 const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
 const [cancelModalIsOpen, setCancelModalIsOpen] = useState(false);
 const [exercise, setExercise] = useState(null);

 // EG-42/AD-8: só o formMethods sobrou. hasPillars, pillarCount e os handlers
 // do wizard de pilares morreram com o rádio e o "Número de pilares".
 const { formMethods } = useCertificationForm();

 const {
  handleSubmit,
  register,
  formState: { errors, isValid, isSubmitting },
 } = formMethods;

 const { data: templates, isLoading: templatesIsLoading } = useSWR(
  `/survey-template/all`,
  fetchData
 );

 // EG-42/AD-3: as questões dos templates escolhidos, na ORDEM de seleção — é
 // essa ordem que o backend transforma no index global (AD-6).
 const [questoesSelecionadas, setQuestoesSelecionadas] = useState([]);

 // Remover aqui é só NÃO criar a questão — nada a ver com a AD-9, que trata de
 // classificação de questão já existente. O botão vem do mesmo card da edição.
 const removerQuestao = (idQuestionTemplate) =>
  setQuestoesSelecionadas((atuais) =>
   atuais.filter((q) => q.id_question_template !== idQuestionTemplate)
  );

 const catalogo = useCatalogoDeClassificacao();

 const estrutura = derivarEstruturaDeTemplates({
  questoes: questoesSelecionadas,
  pilares: catalogo?.pilares,
  temas: catalogo?.temas,
  // Certificado não tem sanfona de tema: a estrutura dele é só pilar. Medido:
  // 754 das 755 questões de certificação não têm tema, então exigir tema aqui
  // jogaria a tela inteira em "Sem classificação".
  exigeTema: false,
 });

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const closeSuccessModal = () => {
  setIsSubmitSuccessful(false);
  navigate('/researches');
 };

 //  const handleNumPilaresChange = (e) => {
 //   const count = parseInt(e.target.value);
 //   setPillarCount(count);
 //   const currentCount = fields.length;

 //   if (count > currentCount) {
 //    // Adiciona novos pilares
 //    for (let i = currentCount; i < count; i++) {
 //     append(
 //      {
 //       pillarName: '',
 //      },
 //      { shouldFocus: false }
 //     );
 //    }
 //    setTabIndex(0);
 //   } else if (count < currentCount) {
 //    for (let i = currentCount - 1; i >= count; i--) {
 //     remove(i);
 //    }
 //    if (tabIndex >= count) {
 //     setTabIndex(count);
 //    }
 //   }
 //  };

 //  const handleHasPillarsChange = (value) => {
 //   setHasPillars(value === 'Sim');
 //  };

 // EG-42/AD-2: handleTabsChange, addQuestion, removeQuestion e
 // handlePillarsConfig morreram com o wizard de pilares. A seleção de
 // perguntas virou SelecaoDeTemplates e a estrutura virou EstruturaDerivada.

 const onSubmit = async (data) => {
  try {
   // EG-401: o botão de finalizar já bloqueia sem exercício; o guard aqui é só
   // para não estourar `.getFullYear()` num submit disparado por outro caminho.
   if (!exercise) return;

   // EG-42/AD-8: o certificado nasce SEM pilares declarados e recebe a estrutura
   // pelo caminho que herda do template.
   //
   // O payload antigo tinha dois formatos, escolhidos pelo rádio "Possui
   // pilares?": com `pillars` (cada um com o id_pillar digitado) ou com
   // `surveyQuestions` plano. Os dois saíram porque a forma passou a ser
   // derivada — e era esse rádio que decidia, sem dizer, o MODELO DE NOTA do
   // certificado (cutoff global x por pilar, certificate.service.ts:1764).
   const surveyResponse = await baseService.post(`/survey/certificate`, {
    description: data.name,
    surveyQuestions: [],
    type: 'certification',
    exercise: exercise.getFullYear(),
   });

   const idSurvey =
    surveyResponse?.data?.id_survey ?? surveyResponse?.data?.survey?.id_survey;

   // A ordem de idsParaGravar é a ordem que o gestor está vendo na estrutura
   // derivada, e é ela que vira o index global no backend (AD-6).
   await baseService.post(`/survey/${idSurvey}/questions/from-templates`, {
    id_question_templates: idsParaGravar(estrutura),
   });

   setIsSubmitSuccessful(true);
  } catch (error) {
   showErrorToast('Não foi possível salvar o questionário');
  }
 };

 if (templatesIsLoading) {
  return <Loader />;
 }

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <Center bg='white' borderBottom={['1px solid #E5E5E5', 'unset']}>
    <Container maxW='1280px' p='32px 0px'>
     <Flex align='center' justify='space-between' gap='4'>
      <Box>
       <Heading fontSize='24px' mb='4'>
        Questionário de Certificação
       </Heading>
       <Flex gap='6'>
        <HStack spacing={0}>
            <InputText
            {...register('name')}
            error={errors.name}
            placeholder='Digite o nome do questionário'
            autoComplete='off'
            w='400px'
            />
            <YearPickerInput
            value={exercise}
            onChange={(date) => setExercise(date)}
            />
        </HStack>
        {/* EG-401: o exercício deixou de ser opcional. É o ano do ciclo, e sem
            ele o questionário de certificação some do dashboard GRI (as medidas
            são datadas por ele). */}
        {!exercise && (
         <Text fontSize='sm' color='#EA582B' mt='6px'>
          Selecione o exercício (ano do ciclo) para continuar.
         </Text>
        )}
        {/*
          EG-42/AD-8: o rádio "Possui pilares?" e o "Número de pilares" saíram.
          A forma do certificado é derivada dos templates e gravada em
          survey.has_pillar na criação. O rádio fazia um segundo trabalho que o
          nome dele não entregava: decidia por qual RÉGUA o certificado é
          avaliado (cutoff global x cutoff por pilar).
        */}
       </Flex>
      </Box>

      <Flex gap='4' justify='end'>
       <Button
        variant='cancel'
        w='133px'
        minW='133px'
        h='59px'
        onClick={() => setCancelModalIsOpen(true)}
       >
        Cancelar
       </Button>

       <Button
        type='submit'
        variant='primary'
        w='129px'
        minW='129px'
        h='59px'
        isLoading={isSubmitting}
        // EG-42/AD-2: "tem pelo menos uma pergunta" não cabe no schema, porque a
       // seleção não é campo do formulário. EG-401: o exercício também não é
       // campo do schema (estado solto). O botão soma as três condições.
       isDisabled={!isValid || questoesSelecionadas.length === 0 || !exercise}
       >
        Finalizar
       </Button>
      </Flex>
     </Flex>
    </Container>
   </Center>

   <Container maxW='1280px' p={0} minH='80vh' my={['8px', '32px']}>
    {/*
      EG-42/AD-2: a tela deixou de ter dois corpos escolhidos pelo rádio (abas de
      pilar OU lista plana). Agora é um só: escolher templates e ver a estrutura
      nascer da classificação das perguntas, incluindo a seção "Sem
      classificação" (AD-4/CA06.1).
    */}
    {/* UM cartão só, como na tela de edição: seleção de templates e estrutura
        derivada no mesmo bloco branco. Dois cartões com vão no meio era a
        diferença visual mais visível contra a edição. */}
    <Box bg='white' borderRadius='12px' overflow='hidden'>
     <Box p='6' borderBottom='2px solid #F5F5FB'>
      <Heading as='h3' fontSize='16px' fontWeight={600}>
       Templates
      </Heading>
     </Box>

     <Box p='6'>
      <SelecaoDeTemplates
       templates={templates}
       templatesIsLoading={templatesIsLoading}
       questoesSelecionadas={questoesSelecionadas}
       onChange={setQuestoesSelecionadas}
       isDisabled={isSubmitting}
      />
     </Box>

     {/* A borda abre a seção da estrutura, como as abas abrem o cartão na tela
         de edição. Sem ela as abas colavam na tag do template. */}
     <Box borderTop='2px solid #F5F5FB' pt='6'>
      <EstruturaDerivada
       estrutura={estrutura}
       exigeTema={false}
       onRemoverQuestao={removerQuestao}
      />
     </Box>
    </Box>
   </Container>

   <CancelModal
    isOpen={cancelModalIsOpen}
    onClose={() => setCancelModalIsOpen(false)}
    title='Deseja mesmo cancelar?'
    content='Ao continuar, todas as informações serão perdidas.'
    isLoading={false}
   />

   <SuccessModal
    isOpen={isSubmitSuccessful}
    onClose={closeSuccessModal}
    title='Questionário criado com sucesso!'
   />
  </form>
 );
}
